/**
 * THE FRUGAL RENDERER — orthographic structure, canvas 2D, no WebGL.
 *
 * FOUNDER, 2026-08-08: "build the frugal 2d renderer... mobile is additional surface, they will have
 * laptops too, idea is give both surfaces."
 *
 * ── THIS IS NOT A DEGRADED 3D VIEW ────────────────────────────────────────────────────────────
 * A marine structural drawing IS orthographic 2D. Profile, plan, transverse section — that is the
 * form the examination uses, the form the candidate must produce by hand, and the form every past
 * paper is set in. The 3D atlas is the teaching aid; this is the exam.
 *
 * So calling this the "fallback" gets the relationship backwards. It is the primary representation,
 * it runs on every device ever made, and it is the one a candidate is actually marked against. The
 * WebGL scene is the enrichment on top (FP-010: reliable floor, best-effort intelligence).
 *
 * ── ONE GEOMETRY, TWO RENDERERS ───────────────────────────────────────────────────────────────
 * This consumes exactly the geometry the 3D renderer consumes. Two implementations of the same
 * teaching content would drift — that is precisely how a weld diagram ended up with 60° recorded as
 * a preparation angle when it is the toe angle, caught only by reconciling two lanes. Because both
 * renderers read the SAME declared members, a difference between them is a renderer bug and can be
 * named as one, rather than two plausible drawings and no way to tell which lies.
 *
 * ── BOTH SURFACES, NOT ONE SCALED ─────────────────────────────────────────────────────────────
 * A laptop is not a large phone. Its advantage is not GPU — it is AREA, which buys the thing that
 * actually teaches: a profile and its section side by side, read together. A phone shows one view at
 * a time and switches. Same renderer, different composition; `pickLayout()` decides, and it decides
 * on available width rather than on a user-agent string, because a phone in landscape on a desk
 * stand deserves the laptop composition.
 */

/** Views a marine drawing is set in. Each is a projection, not a camera. */
export const VIEWS = Object.freeze({
  PROFILE: { id: 'profile', label: 'Profile', axes: ['x', 'z'], flipY: true,
             note: 'Looking from the side. Length forward, depth up.' },
  PLAN:    { id: 'plan',    label: 'Plan',    axes: ['x', 'y'], flipY: false,
             note: 'Looking down. Length forward, breadth across.' },
  SECTION: { id: 'section', label: 'Section', axes: ['y', 'z'], flipY: true,
             note: 'Looking forward at one frame. Breadth across, depth up.' },
});

/**
 * Composition, chosen on WIDTH rather than on a user-agent string — a phone held in landscape on a
 * desk stand has earned the two-up layout, and a narrow window on a laptop has not.
 */
export function pickLayout(width) {
  if (width >= 1024) return { cols: 2, views: [VIEWS.PROFILE, VIEWS.SECTION], labels: 'full' };
  if (width >= 680)  return { cols: 2, views: [VIEWS.PROFILE, VIEWS.SECTION], labels: 'short' };
  return { cols: 1, views: [VIEWS.PROFILE], labels: 'short', switchable: true };
}

/** Fit a bounding box into a canvas with margin, preserving aspect. Returns a world→screen mapper. */
function fitter(members, view, w, h, pad = 28) {
  const [ax, ay] = view.axes;
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const m of members) for (const p of m.points || []) {
    const x = p[ax], y = p[ay];
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
  }
  if (!isFinite(minX)) return null;          // nothing to draw — the caller must say so, not guess
  const dx = (maxX - minX) || 1, dy = (maxY - minY) || 1;
  const s = Math.min((w - pad * 2) / dx, (h - pad * 2) / dy);
  const ox = (w - dx * s) / 2 - minX * s, oy = (h - dy * s) / 2 - minY * s;
  return {
    scale: s,
    to: (p) => [p[ax] * s + ox, view.flipY ? h - (p[ay] * s + oy) : p[ay] * s + oy],
    // metres-per-pixel, so the scale bar states a real length rather than a decorative one
    mpp: 1 / s,
  };
}

/** A scale bar with a ROUND length, because a bar labelled "8.37 m" is decoration, not a scale. */
function niceLength(metres) {
  const pow = Math.pow(10, Math.floor(Math.log10(metres)));
  for (const f of [1, 2, 5, 10]) if (metres <= f * pow) return f * pow;
  return 10 * pow;
}

/**
 * Draw one orthographic view.
 *
 * @param members [{ id, label, layer, points:[{x,y,z}], closed?, weight? }]
 * Members are drawn back-to-front by layer so the shell does not erase the framing behind it.
 */
export function drawView(ctx, { members = [], view = VIEWS.PROFILE, width, height,
                                highlight = null, theme = {}, labels = 'short' } = {}) {
  const ink    = theme.ink    || '#e8eef4';
  const dim    = theme.dim    || '#5d7888';
  const accent = theme.accent || '#38b6e8';
  const bg     = theme.bg     || '#070b11';

  ctx.save();
  ctx.fillStyle = bg; ctx.fillRect(0, 0, width, height);

  const f = fitter(members, view, width, height);
  if (!f) {
    // NOT an empty canvas. A blank drawing area is indistinguishable from a broken one, and the
    // candidate has no way to tell which he is looking at.
    ctx.fillStyle = dim; ctx.font = '13px system-ui, sans-serif'; ctx.textAlign = 'center';
    ctx.fillText('No geometry declared for this view', width / 2, height / 2);
    ctx.restore();
    return { drawn: 0, reason: 'no members carried points for these axes' };
  }

  // Back to front: the shell is drawn last so it reads as the outer boundary it is.
  const order = ['frames', 'girders', 'floors', 'decks', 'bulkheads', 'shell'];
  const sorted = [...members].sort((a, b) =>
    (order.indexOf(a.layer) + 1 || 99) - (order.indexOf(b.layer) + 1 || 99));

  let drawn = 0;
  for (const m of sorted) {
    const pts = (m.points || []).map(f.to);
    if (pts.length < 2) continue;
    const hot = highlight && m.id === highlight;
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
    if (m.closed) ctx.closePath();
    ctx.strokeStyle = hot ? accent : (m.layer === 'shell' ? ink : dim);
    // Line weight carries meaning in a marine drawing: the shell is heavy, framing is light. This
    // is not styling — a candidate reads the hierarchy off the weights.
    ctx.lineWidth = hot ? 2.5 : (m.weight || (m.layer === 'shell' ? 1.8 : 1));
    ctx.stroke();
    drawn++;
  }

  // Scale bar. A drawing without one cannot be read, and this one states a round length.
  const barM = niceLength(f.mpp * Math.min(160, width * 0.3));
  const barPx = barM / f.mpp;
  const bx = 16, by = height - 18;
  ctx.strokeStyle = dim; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(bx, by); ctx.lineTo(bx + barPx, by);
  ctx.moveTo(bx, by - 4); ctx.lineTo(bx, by + 4);
  ctx.moveTo(bx + barPx, by - 4); ctx.lineTo(bx + barPx, by + 4);
  ctx.stroke();
  ctx.fillStyle = dim; ctx.font = '11px system-ui, sans-serif'; ctx.textAlign = 'left';
  ctx.fillText(`${barM} m`, bx + barPx + 8, by + 4);

  if (labels !== 'none') {
    ctx.fillStyle = dim; ctx.font = '11px system-ui, sans-serif';
    ctx.fillText(view.label.toUpperCase(), 16, 22);
    if (labels === 'full') { ctx.fillStyle = dim; ctx.fillText(view.note, 16, 38); }
  }

  ctx.restore();
  return { drawn, scale_m_per_px: f.mpp };
}

/**
 * Mount a view onto a canvas, DPR-correct and resize-aware.
 *
 * Redraw is scheduled on a frame rather than run inside the ResizeObserver: resizing fires a burst
 * of callbacks and drawing inside one of them can trigger another, which is how a layout loop
 * starts. Returns a handle with update() and destroy().
 */
export function mountView(canvas, opts = {}) {
  let state = { ...opts };
  let raf = 0;

  const paint = () => {
    raf = 0;
    const w = canvas.clientWidth, h = canvas.clientHeight;
    if (!w || !h) return;
    const dpr = Math.min(devicePixelRatio || 1, 2);   // above 2 costs fill rate and buys nothing
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
    }
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    state.last = drawView(ctx, { ...state, width: w, height: h });
  };
  const schedule = () => { if (!raf) raf = requestAnimationFrame(paint); };

  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
  ro?.observe(canvas);
  schedule();

  return {
    update(next) { state = { ...state, ...next }; schedule(); },
    get result() { return state.last; },
    destroy() { ro?.disconnect(); if (raf) cancelAnimationFrame(raf); },
  };
}
